import type { InternalAxiosRequestConfig } from 'axios';

import { getGhostKind } from './dev-ghost';

/**
 * DEV-ONLY ghost-mode API mocks. Browsing without a token 401s every call, so
 * data pages (Billing, API Keys, …) render empty. When ghost mode is active
 * these canned responses let those pages render with realistic content so they
 * can be design-reviewed. Real, known-shape data for the high-value endpoints;
 * safe empty shapes for the rest (pages then show their real empty states, not
 * a crash). Entire module is import.meta.env.DEV-gated at the call site and
 * tree-shaken from production builds.
 */

type PageMock = (path: string) => unknown | undefined;

/** Mocks a page owns for its own endpoints, added by the app (shared code does not import pages). */
const pageMocks: Array<{ segment: string; mock: PageMock }> = [];

/**
 * Adds a page's ghost-mode mocks for GET paths that contain `segment`; they answer after the ones
 * here. Call it behind import.meta.env.DEV so production builds drop the page's mocks.
 */
export function registerGhostMock(segment: string, mock: PageMock): void {
  pageMocks.push({ segment, mock });
}

const NOW = Date.parse('2026-07-05T12:00:00Z');
const iso = (daysAgo: number) => new Date(NOW - daysAgo * 86_400_000).toISOString();

const TEAM = {
  id: 1,
  name: 'Acme Studio',
  // 640 with the 14-day usage dataset below ≈ 10 days of runway — lands in
  // the "getting low" band so the runway strip demos its amber state.
  credits: 640,
  account_status: 'approved',
  company_name: 'Acme Studio',
  company_email: 'billing@acme.com',
  company_country: 'US',
  company_address: '1 Market St, Suite 400',
  company_postcode: '94105',
  company_tax_id: 'EIN 12-3456789',
  contact_telegram: 'acme',
  team_members: [
    { id: 'm1', email: 'sandy@example.com', role: 'admin' },
    { id: 'm2', email: 'dev@acme.com', role: 'member' },
  ],
};

const API_KEYS = [
  {
    id: 1,
    aurous_id: 'key_01hx8mq3',
    name: 'Production',
    is_active: true,
    scopes: ['images:write', 'loras:read'],
    key_prefix: 'al_live_9f2c',
    last_used_at: iso(0),
    created_at: iso(21),
    created_by_user_id: 'u1',
    creator: { email: 'sandy@example.com' },
  },
  {
    id: 2,
    aurous_id: 'key_01hy2ka7',
    name: 'Staging',
    is_active: true,
    scopes: ['images:write'],
    key_prefix: 'al_test_1a4b',
    last_used_at: iso(3),
    created_at: iso(9),
    created_by_user_id: 'u1',
    creator: { email: 'sandy@example.com' },
  },
];

const PACKAGES = [
  { id: 1, name: 'Starter', credits: 5000, price_cents: 3000, currency: 'usd' },
  { id: 2, name: 'Studio', credits: 20000, price_cents: 10000, currency: 'usd' },
  { id: 3, name: 'Scale', credits: 60000, price_cents: 25000, currency: 'usd' },
];

// 14 days of plausible usage (newest last, one zero-day) — drives the Usage
// charts and gives the credit-runway math something real to chew on.
const DAILY_CREDITS = [62, 48, 71, 55, 0, 38, 84, 66, 52, 90, 47, 73, 58, 65];
const USAGE_BY_DATE = DAILY_CREDITS.map((cost, i) => ({
  date: new Date(NOW - (DAILY_CREDITS.length - 1 - i) * 86_400_000).toISOString().slice(0, 10),
  cost,
}));

const USAGE_DATA = {
  totalCost: DAILY_CREDITS.reduce((a, b) => a + b, 0), // 809
  interval: 'day',
  usageByDate: USAGE_BY_DATE,
  usageByKey: [
    { apiKeyId: '1', cost: 490 },
    { apiKeyId: '2', cost: 319 },
  ],
  usageByUser: [
    { userId: 'm1', cost: 519 },
    { userId: 'm2', cost: 290 },
  ],
  usageByLora: [
    { loraId: 'l1', loraName: 'Neon Portrait', cost: 305 },
    { loraId: 'l2', loraName: 'Product Studio', cost: 214 },
  ],
};

const USAGE_TIMESERIES = {
  object: 'list',
  data: USAGE_BY_DATE.map(({ date, cost }) => ({
    object: 'usage.bucket',
    bucket_start: `${date}T00:00:00Z`,
    bucket_end: `${date}T23:59:59Z`,
    groups: [
      {
        key: {},
        metrics: {
          request_count: Math.round(cost / 2),
          successful_count: Math.max(0, Math.round(cost / 2) - (cost > 60 ? 2 : 0)),
          failed_count: cost > 60 ? 1 : 0,
          cancelled_count: cost > 60 ? 1 : 0,
          credits_used: cost,
          duration_ms_p50: cost ? 3200 : null,
          duration_ms_p95: cost ? 8400 : null,
          image_count: Math.round(cost / 2),
          video_seconds: 0,
          total_input_tokens: 0,
          total_output_tokens: 0,
        },
      },
    ],
  })),
  has_more: false,
  next_page: null,
};

/** Returns mock data for a GET path, or `undefined` to let the call proceed. */
export function ghostMock(config: InternalAxiosRequestConfig): unknown | undefined {
  if (!import.meta.env.DEV || getGhostKind() === null) return undefined;
  if ((config.method ?? 'get').toLowerCase() !== 'get') return undefined;

  const path = (config.url ?? '').split('?')[0].replace(/\/+$/, '');

  if (path.endsWith('/team')) return [TEAM];
  if (/\/api-key\/team\/\d+$/.test(path)) return API_KEYS;
  if (path.endsWith('/packages')) return PACKAGES;
  if (path.endsWith('/auth/me')) return { ...TEAM, email: 'sandy@example.com' };

  if (/\/usage\/team\/\d+$/.test(path)) return USAGE_DATA;
  if (path.endsWith('/dashboard/usage/timeseries')) return USAGE_TIMESERIES;

  // Safe empty shapes — pages render their real empty state rather than crash.
  if (path.endsWith('/payment-method')) return [];
  if (/\/team\/\d+\/billing$/.test(path)) return [];
  if (/\/team\/\d+\/invites$/.test(path)) return [];
  if (path.endsWith('/dashboard/usage/generations'))
    return { object: 'list', data: [], has_more: false, next_cursor: null };
  if (path.endsWith('/inference')) return { data: [], total: 0, page: 1 };
  if (path.endsWith('/character')) return { items: [], total: 0 };
  if (
    path.endsWith('/lora') ||
    path.endsWith('/lora/featured') ||
    path.endsWith('/lora/bundles') ||
    path.endsWith('/video-loras')
  )
    return [];
  if (path.endsWith('/admin/invoices')) return { data: [], total: 0 };

  for (const { segment, mock } of pageMocks) if (path.includes(segment)) return mock(path);

  return undefined;
}
